/**
 * TreatmentNoteDialog — picker + dynamic-form editor for treatment notes.
 *
 * Two modes:
 *   1. ``noteId=null``  → picker of published templates. On select, a
 *                        new note is created (backend snapshots template)
 *                        and we transition into edit mode.
 *   2. Editing an existing draft (or previewing a finalised note) —
 *      renders each section's fields honouring type + conditional
 *      visibility. Debounced autosave on every value change so the
 *      practitioner can't lose work mid-consult.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, CheckCircle2, FileText, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import BodyChart, { BodyChartLegend } from "@/components/BodyChart";

export default function TreatmentNoteDialog({
  email, noteId, initialSummaryBlock, onClose, onSaved,
  // Phase 2.5: when provided, we try to auto-resolve a note template
  // linked to this Acuity appointment type. If we get a match, we
  // skip the picker entirely and drop the practitioner straight
  // into the correct template. Backwards compat: leaving this
  // undefined preserves the existing picker-first flow.
  appointmentTypeId,
}) {
  const [templates, setTemplates] = useState([]);
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finalising, setFinalising] = useState(false);
  const [savingLabel, setSavingLabel] = useState("");
  const saveTimer = useRef(null);
  const [measureRegistry, setMeasureRegistry] = useState([]);
  const [autoPickedFrom, setAutoPickedFrom] = useState(null); // template name for the toast

  // Bootstrap — fetch published templates + registry OR the existing note.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (noteId) {
          const r = await api.get(`/health-record/notes/${noteId}`);
          if (!cancelled) setNote(r.data);
        } else {
          // Phase 2.5 — before showing the picker, try to auto-pick.
          if (appointmentTypeId) {
            try {
              const r = await api.get("/note-templates/resolve", {
                params: { appointment_type_id: appointmentTypeId },
              });
              if (!cancelled && r.data?.id) {
                // Skip the picker: start the note straight away.
                const started = await api.post(
                  `/health-record/${encodeURIComponent(email)}/notes`,
                  {
                    appointment_type_id: appointmentTypeId,
                    initial_summary_block: initialSummaryBlock || null,
                  },
                );
                if (!cancelled) {
                  setNote(started.data);
                  setAutoPickedFrom(r.data.name);
                }
                // We also need the measure registry for outcome-measure
                // rendering.
                try {
                  const m = await api.get("/outcome-measures");
                  if (!cancelled) setMeasureRegistry(m.data?.measures || []);
                } catch { /* ignore */ }
                return;
              }
            } catch {
              // 404 → no template linked. Fall through to picker.
            }
          }
          const [t, m] = await Promise.all([
            api.get("/note-templates"),
            api.get("/outcome-measures"),
          ]);
          if (!cancelled) {
            // Only show published, non-archived templates in the picker.
            setTemplates((t.data?.templates || []).filter((x) => x.published && !x.archived));
            setMeasureRegistry(m.data?.measures || []);
          }
        }
      } catch (e) {
        toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [noteId, appointmentTypeId, email, initialSummaryBlock]);

  const readOnly = note?.status === "finalised";

  // Surface a one-time toast telling the user which template was
  // auto-picked (and let them switch templates by cancelling the
  // note if it's wrong).
  useEffect(() => {
    if (autoPickedFrom) {
      toast.success(`Auto-selected template: ${autoPickedFrom}`, {
        description: "Cancel the note to pick a different template.",
        duration: 5000,
      });
    }
  }, [autoPickedFrom]);

  async function pickTemplate(tpl) {
    setLoading(true);
    try {
      const r = await api.post(`/health-record/${encodeURIComponent(email)}/notes`, {
        template_id: tpl.id,
        initial_summary_block: initialSummaryBlock || null,
      });
      setNote(r.data);
    } catch (e) {
      toast.error("Could not start note", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }

  const setValue = useCallback((fieldId, value) => {
    setNote((n) => ({ ...n, values: { ...(n?.values || {}), [fieldId]: value } }));
    setSavingLabel("Saving…");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.patch(`/health-record/notes/${note.id}`, { values: { [fieldId]: value } });
        setSavingLabel("Saved");
        setTimeout(() => setSavingLabel(""), 1200);
      } catch (e) {
        setSavingLabel("Save failed");
        toast.error("Autosave failed", { description: e?.response?.data?.detail || e.message });
      }
    }, 700);
  }, [note?.id]);

  const [missingFields, setMissingFields] = useState([]);

  async function finalise() {
    if (!window.confirm("Finalise this note? It becomes read-only.")) return;
    setFinalising(true);
    // Flush any pending autosave first.
    if (saveTimer.current) clearTimeout(saveTimer.current);
    try {
      // Full sync of the merged values in case debounce hasn't fired.
      await api.patch(`/health-record/notes/${note.id}`, { values: note.values || {} });
      const r = await api.post(`/health-record/notes/${note.id}/finalise`);
      setNote(r.data);
      setMissingFields([]);
      toast.success("Note finalised");
      onSaved?.();
    } catch (e) {
      // Backend returns 422 with {detail: {error: "missing_required", missing: [...]}}
      // when required fields are empty. Highlight + surface the list so
      // the practitioner can jump straight to what's missing.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 422 && detail?.error === "missing_required") {
        const missing = detail.missing || [];
        setMissingFields(missing);
        toast.error(
          `Cannot finalise — ${missing.length} required field${missing.length === 1 ? "" : "s"} empty`,
          { description: missing.map((m) => m.field_label).slice(0, 3).join(", ")
                        + (missing.length > 3 ? ` +${missing.length - 3} more` : "") },
        );
        // Scroll the first missing field into view.
        setTimeout(() => {
          const first = missing[0];
          if (first) {
            const el = document.querySelector(`[data-tnote-field="${first.field_id}"]`);
            el?.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 100);
      } else {
        toast.error("Finalise failed", { description: detail || e.message });
      }
    } finally {
      setFinalising(false);
    }
  }

  const missingFieldIds = useMemo(
    () => new Set(missingFields.map((m) => m.field_id)),
    [missingFields],
  );
  const missingSectionIds = useMemo(
    () => new Set(missingFields.map((m) => m.section_id)),
    [missingFields],
  );

  // Clear a field's "missing" flag as soon as the user starts filling it in.
  const handleValueChange = useCallback((fieldId, value) => {
    if (missingFieldIds.has(fieldId)) {
      setMissingFields((rows) => rows.filter((m) => m.field_id !== fieldId));
    }
    setValue(fieldId, value);
  }, [missingFieldIds, setValue]);

  const sections = note?.template_snapshot?.sections || [];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="treatment-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-zinc-500" />
            {note ? (note.template_name || "Treatment note") : "New treatment note"}
            {note?.status === "finalised" && (
              <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-800">
                finalised
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs flex items-center gap-3">
            {note ? (
              <>
                <span>Template v{note.template_version}</span>
                {savingLabel && (
                  <span className="text-zinc-400 inline-flex items-center gap-1">
                    {savingLabel === "Saved"
                      ? <><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Saved</>
                      : <><Loader2 className="h-3 w-3 animate-spin" /> {savingLabel}</>}
                  </span>
                )}
              </>
            ) : "Pick a template to start."}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 pr-1 -mr-1">
          {loading ? (
            <div className="p-6 text-sm text-zinc-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !note ? (
            <TemplatePicker templates={templates} onPick={pickTemplate} initialSummaryBlock={initialSummaryBlock} />
          ) : (
            <>
              {note.initial_summary_block && (
                <div
                  className="mb-3 p-3 rounded-md border border-indigo-200 bg-indigo-50/60 text-xs text-indigo-950 whitespace-pre-wrap"
                  data-testid="tnote-ai-summary-block"
                >
                  <div className="text-2xs uppercase tracking-wider font-semibold text-indigo-700 mb-1">
                    AI current picture (read-only)
                  </div>
                  {note.initial_summary_block}
                </div>
              )}
              {missingFields.length > 0 && (
                <div
                  className="mb-3 p-3 rounded-md border border-red-300 bg-red-50 text-xs text-red-900"
                  data-testid="tnote-missing-required-banner"
                >
                  <div className="flex items-center gap-1.5 font-semibold mb-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {missingFields.length} required field{missingFields.length === 1 ? "" : "s"} still empty
                  </div>
                  <ul className="list-disc ml-4 space-y-0.5">
                    {missingFields.map((m) => (
                      <li key={m.field_id}>
                        <button
                          className="underline hover:no-underline"
                          onClick={() => {
                            const el = document.querySelector(`[data-tnote-field="${m.field_id}"]`);
                            el?.scrollIntoView({ behavior: "smooth", block: "center" });
                          }}
                          data-testid={`tnote-missing-jump-${m.field_id}`}
                        >
                          {m.section_name ? `${m.section_name} · ` : ""}{m.field_label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <NoteFormRenderer
                sections={sections}
                values={note.values || {}}
                measureRegistry={measureRegistry}
                readOnly={readOnly}
                onValueChange={handleValueChange}
                missingFieldIds={missingFieldIds}
                missingSectionIds={missingSectionIds}
              />
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {note && !readOnly && (
            <Button
              onClick={finalise}
              disabled={finalising}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="tnote-finalise"
            >
              {finalising ? "Finalising…" : "Finalise & lock"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function TemplatePicker({ templates, onPick, initialSummaryBlock }) {
  if (templates.length === 0) {
    return (
      <div className="space-y-2">
        {initialSummaryBlock && (
          <div className="p-2.5 rounded-md border border-indigo-200 bg-indigo-50/70 text-2xs text-indigo-900" data-testid="tnote-picker-summary-preview">
            <span className="font-semibold">AI current picture</span> will be attached once a template is published.
          </div>
        )}
        <div className="p-6 text-center text-sm text-zinc-500">
          No published templates yet. Ask an admin to create one under
          <span className="mx-1 font-medium">Settings → Note templates</span>.
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {initialSummaryBlock && (
        <div className="p-2.5 rounded-md border border-indigo-200 bg-indigo-50/70 text-2xs text-indigo-900" data-testid="tnote-picker-summary-preview">
          <span className="font-semibold">AI current picture</span> will be attached at the top of the new note.
        </div>
      )}
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-1" data-testid="tnote-template-picker">
      {templates.map((t) => (
        <li key={t.id}>
          <button
            onClick={() => onPick(t)}
            className="text-left w-full p-3 border border-zinc-200 rounded-md hover:border-zinc-400 hover:shadow-sm transition-all"
            data-testid={`tnote-pick-${t.id}`}
          >
            <div className="font-medium text-sm text-zinc-950">{t.name}</div>
            <div className="text-2xs text-zinc-500 line-clamp-2 mt-0.5">
              {t.description || "\u00A0"}
            </div>
            <div className="text-2xs uppercase tracking-wider text-zinc-400 mt-1">v{t.version}</div>
          </button>
        </li>
      ))}
    </ul>
    </div>
  );
}


function NoteFormRenderer({ sections, values, measureRegistry, readOnly, onValueChange, missingFieldIds, missingSectionIds }) {
  const missingFields = missingFieldIds || new Set();
  const missingSections = missingSectionIds || new Set();
  return (
    <div className="space-y-4 p-1" data-testid="tnote-form">
      {sections.map((s) => (
        <div
          key={s.id}
          className={`border rounded-md p-3 ${missingSections.has(s.id) ? "border-red-300 bg-red-50/40" : "border-zinc-200"}`}
        >
          <div className="text-sm font-semibold text-zinc-950">{s.name}</div>
          {s.description && <div className="text-2xs text-zinc-500 mb-2">{s.description}</div>}
          <div className="space-y-3 mt-2">
            {s.fields
              .filter((f) => isFieldVisible(f, values))
              .map((f) => (
                <FieldRenderer
                  key={f.id}
                  field={f}
                  value={values[f.id]}
                  measureRegistry={measureRegistry}
                  readOnly={readOnly}
                  onChange={(v) => onValueChange(f.id, v)}
                  isMissing={missingFields.has(f.id)}
                />
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}


// Match the server-side helper in health_record/templates.py::field_visible
function isFieldVisible(field, values) {
  const showIf = field?.conditional?.show_if || null;
  if (!showIf) return true;
  return Object.entries(showIf).every(([otherId, allowed]) => {
    const actual = values[otherId];
    const list = Array.isArray(allowed) ? allowed : [allowed];
    if (Array.isArray(actual)) return list.some((a) => actual.includes(a));
    return list.includes(actual);
  });
}


function FieldRenderer({ field, value, measureRegistry, readOnly, onChange, isMissing }) {
  const testid = `tnote-field-${field.id}`;
  const label = (
    <Label className="text-xs flex items-center gap-1">
      {field.label}
      {field.required && <span className="text-red-600">*</span>}
      {isMissing && (
        <span
          className="ml-1 text-2xs uppercase tracking-wider px-1 py-0.5 rounded bg-red-100 text-red-700"
          data-testid={`tnote-missing-tag-${field.id}`}
        >
          required
        </span>
      )}
    </Label>
  );
  const wrap = (children) => (
    <div
      data-tnote-field={field.id}
      className={isMissing ? "rounded-md p-1.5 -m-1.5 ring-2 ring-red-300 bg-red-50/50" : ""}
    >
      {children}
    </div>
  );

  if (field.type === "textarea") {
    return wrap(
      <>
        {label}
        <Textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={readOnly} data-testid={testid} />
        {field.help && <div className="text-2xs text-zinc-500 mt-0.5">{field.help}</div>}
      </>
    );
  }
  if (field.type === "number") {
    return wrap(
      <>
        {label}
        <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))} disabled={readOnly} data-testid={testid} />
      </>
    );
  }
  if (field.type === "date") {
    return wrap(
      <>
        {label}
        <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={readOnly} data-testid={testid} />
      </>
    );
  }
  if (field.type === "select" || field.type === "radio") {
    return wrap(
      <>
        {label}
        <select
          value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={readOnly}
          className="mt-1 h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white w-full"
          data-testid={testid}
        >
          <option value="">(none)</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </>
    );
  }
  if (field.type === "multiselect") {
    const selected = Array.isArray(value) ? value : [];
    return wrap(
      <>
        {label}
        <div className="flex flex-wrap gap-1 mt-1">
          {(field.options || []).map((o) => (
            <button
              key={o} type="button" disabled={readOnly}
              onClick={() => {
                if (readOnly) return;
                onChange(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]);
              }}
              className={`text-xs px-2 py-1 rounded-full border ${selected.includes(o) ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-600 border-zinc-300"}`}
            >
              {o}
            </button>
          ))}
        </div>
      </>
    );
  }
  if (field.type === "checkbox") {
    return (
      <div data-tnote-field={field.id} className={`flex items-center gap-2 ${isMissing ? "rounded-md p-1.5 -m-1.5 ring-2 ring-red-300 bg-red-50/50" : ""}`}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} disabled={readOnly} data-testid={testid} />
        <Label className="text-xs">{field.label}</Label>
      </div>
    );
  }
  if (field.type === "outcome_measure") {
    const measure = measureRegistry.find((m) => m.id === field.measure_id);
    return wrap(
      <>
        <Label className="text-xs">{field.label}{measure && <span className="text-zinc-500 ml-1">({measure.unit})</span>}{field.required && <span className="text-red-600 ml-1">*</span>}{isMissing && <span className="ml-1 text-2xs uppercase tracking-wider px-1 py-0.5 rounded bg-red-100 text-red-700">required</span>}</Label>
        <Input
          type="number" step="0.1" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))}
          min={measure?.min ?? undefined} max={measure?.max ?? undefined}
          disabled={readOnly} data-testid={testid}
        />
        <div className="text-2xs text-zinc-500 mt-0.5">Direction: {measure?.direction === "higher" ? "higher = better" : measure?.direction === "lower" ? "lower = better" : "—"}</div>
      </>
    );
  }
  if (field.type === "body_chart") {
    const markers = Array.isArray(value) ? value : [];
    return wrap(
      <>
        {label}
        <BodyChart markers={markers} interactive={!readOnly} onChange={onChange} />
        {!readOnly && <div className="mt-1"><BodyChartLegend /></div>}
      </>
    );
  }
  // default: text
  return wrap(
    <>
      {label}
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={readOnly} data-testid={testid} />
    </>
  );
}
